import { LngLat } from "maplibre-gl";
import { useState } from "react";

import { getWmsRequest } from "@nens/react-map-gl/utils";
import { convertLizardBoundsToMapExtent } from "@nens/shared-utils";

import { VisibleLayer } from "@/components/layer-control";
import { useRasterWithColorRange } from "@/lib/rana-raster-details/hooks/use-raster-with-color-range";
import { useFileDescriptorViewQuery } from "@/queries/query-file-descriptors";
import { RasterFileDescriptor } from "@/types/descriptor-types";
import { getLegendUnit } from "@/utils/get-legend-unit";

export function useRasterPreview(descriptor: RasterFileDescriptor, title: string) {
  const [opacity, setOpacity] = useState(1);
  const [visibleLayers, setVisibleLayers] = useState<VisibleLayer[]>([
    { descriptorId: descriptor.id, layer: title },
  ]);
  const [point, setPoint] = useState<LngLat | null>(null);

  const {
    data: noBoundsRaster,
    isSuccess,
    isPending,
  } = useFileDescriptorViewQuery(descriptor.id, "lizard-raster");

  const { raster, setDefaultColor, setColorRange, setColorBounds } = useRasterWithColorRange(
    descriptor,
    noBoundsRaster
  );

  if (!isSuccess || !raster)
    return {
      isPending,
      isSuccess,
    };

  const { wms_info, options, spatial_bounds } = raster;
  const { layer } = wms_info;
  let { styles } = options;

  if (descriptor.meta.styleId) {
    // Styles is a string, possibly a : separated list of fields.
    // Replace the first with styleId, keep the rest (e.g. color fields) intact.
    const stylesParts = styles ? styles.split(":") : [];
    stylesParts[0] = `RANA_STYLE__${descriptor.meta.styleId}`;
    styles = stylesParts.join(":");
  }

  const bounds = convertLizardBoundsToMapExtent(spatial_bounds);

  const unit = getLegendUnit(descriptor);

  const wmsUrl = `/v1-alpha/tenants/${descriptor.tenant}/file-descriptors/${descriptor.id}/wms`;

  const tiles = getWmsRequest(wmsUrl, {
    layer,
    styles,
    transparent: false,
  });

  return {
    isPending,
    isSuccess,
    bounds,
    layer,
    styles,
    tiles,
    unit,
    visibleLayers,
    setVisibleLayers,
    point,
    setPoint,
    setDefaultColor,
    setColorRange,
    setColorBounds,
    setOpacity,
    opacity,
  } as const;
}
